/**
 * ============================================================
 * JDL — Événements nommés pour Microsoft Clarity
 * ------------------------------------------------------------
 * Transforme les clics sur NOS éléments (carrousel, liens moment,
 * quiz, filtres) en événements Clarity filtrables :
 *   clarity('event', 'xs_produit')   …
 * et marque chaque page avec son type :
 *   clarity('set', 'jdl_page', 'collection_moment')
 *
 * Principes :
 * - Une seule table de règles (RULES) : ajouter un suivi = ajouter une ligne.
 * - Écoute déléguée sur le document : fonctionne aussi pour le contenu
 *   ajouté après coup (résultats du quiz, fenêtre « Choisir des options »).
 * - RGPD : AUCUNE donnée personnelle, aucun texte saisi, aucune URL :
 *   seulement un nom d'événement fixe. Le consentement est géré par
 *   Clarity lui-même (application Shopify) ; ce fichier ne pose aucun cookie.
 * - Si Clarity est absent ou bloqué, les appels restent dans une file en
 *   mémoire (même mécanisme que le script officiel) et rien ne casse.
 * ============================================================
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api; // tests Node
  else api.boot(window, document, document.currentScript);
})(this, function () {
  'use strict';

  /**
   * Règles de clic : la première qui correspond gagne (ordre = priorité).
   * - selector : élément cliqué (ou un de ses parents)
   * - event    : nom envoyé à Clarity (court, sans accent)
   * - once     : n'envoyer qu'une fois par page (évite le bruit)
   * - resolve  : (optionnel) fonction(élément, état) → nom d'événement, ou null pour passer à la règle suivante
   *
   * Particularités Dawn prises en compte :
   * - le bouton « Choisir des options » porte aussi name="add" → testé AVANT l'ajout direct ;
   * - la fenêtre d'options (quick-add-modal) est déplacée en fin de <body> par Dawn :
   *   on mémorise les fenêtres ouvertes depuis le carrousel pour attribuer leur ajout au carrousel.
   */
  var RULES = [
    // --- Carrousel « Également dans la légende… » (fiche produit) ---
    {
      selector: '.jdl-xs modal-opener', // ouverture « Choisir des options » (produit à tailles)
      resolve: function (hit, state) {
        var modal = hit.getAttribute('data-modal');
        if (modal) state.xsModals[modal] = true;
        return 'xs_options';
      }
    },
    { selector: '.jdl-xs [name="add"]', event: 'xs_ajout' },               // ajout direct (produit sans taille)
    {
      selector: 'quick-add-modal [name="add"]', // ajout depuis une fenêtre d'options
      resolve: function (hit, state) {
        var modal = hit.closest('quick-add-modal');
        return modal && state.xsModals['#' + modal.id] ? 'xs_ajout' : 'ajout_rapide';
      }
    },
    { selector: '.jdl-xs .jdl-xs__all', event: 'xs_voir_tout' },           // carte noire finale
    { selector: '.jdl-xs .slider-button', event: 'xs_defilement', once: true },
    { selector: '.jdl-xs a.full-unstyled-link', event: 'xs_produit' },

    // --- Fiche produit : bouton principal (référence pour comparer au carrousel) ---
    { selector: 'product-form [name="add"]', event: 'fiche_ajout' },

    // --- Guide des tailles (snippets/size-guide-drawer.liquid) — 21/10/2026 ---
    // Entonnoir : ouverture → mesures consultées → taille choisie via le recommandeur.
    // Aucune valeur saisie (taille, poids) n'est envoyée : seulement le nom de l'événement.
    { selector: '#jdl-sg-trigger, [data-jdl-sg-open]', event: 'guide_ouverture' },
    { selector: '#jdl-sg-table-toggle', event: 'guide_mesures', once: true },
    { selector: '#jdl-sg-apply', event: 'guide_taille_choisie' },

    // --- Collections moment ---
    { selector: '.jdl-story-link', event: 'moment_histoire' },
    { selector: '.jdl-back-link', event: 'moment_retour' },

    // --- Quiz cadeau ---
    { selector: '.jdl-gf .jdl-gf__choice', event: 'quiz_debut', once: true },
    { selector: '.jdl-gf .jdl-gf__results .jdl-gf__card', event: 'quiz_produit' },
    { selector: '.jdl-gf .jdl-gf__actions a.jdl-gf__btn', event: 'quiz_catalogue' },
    { selector: '.jdl-gf .jdl-gf__actions button.jdl-gf__btn', event: 'quiz_recommencer' },

    // --- Blocs produits dans les articles (assets/jdl-blog-module.js) — 21/10/2026 ---
    // On distingue le bloc du haut (juste après l'introduction) des blocs du milieu
    // pour savoir lequel déclenche les clics : bloc_haut_* / bloc_milieu_*.
    { selector: '.jdl-module a.jdl-module__product', resolve: function (hit) { return blocEvent(hit, 'produit'); } },
    { selector: '.jdl-module .jdl-module__cta, .jdl-module > p > a', resolve: function (hit) { return blocEvent(hit, 'voir'); } },
    { selector: '.jdl-module .jdl-module__nav', event: 'bloc_defilement', once: true },

    // --- Encart « idée cadeau » (sections/jdl-gift-nudge.liquid) ---
    // encart_affiche est envoyé par assets/jdl-gift-nudge.js au moment de l'affichage.
    { selector: '#jdl-nudge .jdl-nudge__link', event: 'encart_clic' },
    { selector: '#jdl-nudge .jdl-nudge__close', event: 'encart_ferme' },

    // --- Barre de filtres des collections ---
    { selector: '.jdl-ff .jdl-ff__apply', event: 'filtre_ok' },
    { selector: '.jdl-ff a.jdl-ff__option', event: 'filtre_budget' },
    { selector: '.jdl-ff .jdl-ff__reset, .jdl-ff .jdl-ff__clear', event: 'filtre_effacer' },
    { selector: '.jdl-ff .jdl-ff__chip', event: 'filtre_badge_retire' }
  ];

  /**
   * Nom d'événement d'un bloc d'article selon sa position.
   * Premier bloc de l'article = « haut », les suivants = « milieu ».
   */
  function blocEvent(hit, action) {
    var bloc = hit.closest('.jdl-module');
    var doc = hit.ownerDocument;
    var first = doc && doc.querySelector('.jdl-module');
    return 'bloc_' + (bloc && bloc === first ? 'haut' : 'milieu') + '_' + action;
  }

  /** Types de page autorisés (valeur posée par snippets/jdl-clarity.liquid). */
  var PAGE_TYPES = [
    'accueil', 'produit_vetement', 'produit_accessoire', 'produit_autre',
    'collection_moment', 'collection_catalogue', 'collection',
    'quiz', 'page', 'article', 'panier', 'recherche', 'autre'
  ];

  /** État propre à une page (fenêtres d'options ouvertes depuis le carrousel). */
  function newState() {
    return { xsModals: {} };
  }

  /**
   * Trouve l'événement correspondant à un élément cliqué.
   * @param {Element} target élément cliqué
   * @param {Object} [state] état de la page (créé si absent)
   * @returns {{event: string, once: boolean}|null}
   */
  function matchRule(target, state) {
    if (!target || typeof target.closest !== 'function') return null;
    state = state || newState();
    for (var i = 0; i < RULES.length; i++) {
      var r = RULES[i];
      var hit = target.closest(r.selector);
      if (!hit) continue;
      var name = r.resolve ? r.resolve(hit, state) : r.event;
      if (!name) continue;
      return { event: name, once: !!r.once };
    }
    return null;
  }

  /** Garantit window.clarity (file d'attente identique au script officiel Clarity). */
  function ensureClarity(win) {
    win.clarity = win.clarity || function () {
      (win.clarity.q = win.clarity.q || []).push(arguments);
    };
    return win.clarity;
  }

  /**
   * Branche le suivi sur la page.
   * @param {Window} win
   * @param {Document} doc
   * @param {HTMLScriptElement|null} script balise <script> porteuse de data-jdl-page
   */
  function boot(win, doc, script) {
    var sent = {}; // événements « once » déjà envoyés sur cette page
    var state = newState();

    // Correctif 21/10/2026 : on relit window.clarity À CHAQUE envoi.
    // Avant, la fonction provisoire était mémorisée au démarrage ; une fois le vrai
    // Clarity chargé (il remplace window.clarity et ne relit plus la file), tous
    // les clics partaient dans une file ignorée → événements perdus.
    function call() {
      var args = arguments;
      try { ensureClarity(win).apply(null, args); } catch (e) { /* Clarity indisponible : on ignore */ }
    }
    function send(name) {
      call('event', name);
    }

    // 1. Type de page (liste fermée : jamais de valeur libre envoyée)
    var page = script && script.getAttribute('data-jdl-page');
    if (PAGE_TYPES.indexOf(page) === -1) page = 'autre';
    call('set', 'jdl_page', page);

    // 2. Clics (écoute déléguée, phase de capture : passe avant les scripts qui rechargent la page)
    doc.addEventListener('click', function (e) {
      var m = matchRule(e.target, state);
      if (!m) return;
      if (m.once) {
        if (sent[m.event]) return;
        sent[m.event] = true;
      }
      send(m.event);
    }, true);

    // 3. Quiz terminé : les résultats sont construits en JavaScript, on observe leur apparition
    var stages = doc.querySelectorAll('.jdl-gf [data-stage]');
    if (stages.length && typeof win.MutationObserver === 'function') {
      var observer = new win.MutationObserver(function () {
        if (doc.querySelector('.jdl-gf .jdl-gf__results')) {
          var empty = !doc.querySelector('.jdl-gf .jdl-gf__results .jdl-gf__card');
          send(empty ? 'quiz_sans_resultat' : 'quiz_resultat');
        }
      });
      for (var i = 0; i < stages.length; i++) observer.observe(stages[i], { childList: true });
    }

    return { page: page };
  }

  return { RULES: RULES, PAGE_TYPES: PAGE_TYPES, newState: newState, matchRule: matchRule, ensureClarity: ensureClarity, boot: boot };
});
